function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable react/prop-types */

import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useAppState, useAppDispatch } from '@components/common/context/app';
import { get } from '@evershop/evershop/src/lib/util/get';
export default function FloatingCart(_ref) {
  var {
    cartUrl = '/cart',
    cart,
    setting
  } = _ref;
  var appContext = useAppState();
  var {
    setData
  } = useAppDispatch();
  var miniCart = get(appContext, 'cart', cart || {});
  var controls = useAnimation();
  var prevQtyRef = useRef(0);
  var isInitialMount = useRef(true);

  // Defense against "Objects are not valid as a React child"
  var contextQty = typeof (miniCart === null || miniCart === void 0 ? void 0 : miniCart.totalQty) === 'object' ? 0 : parseInt((miniCart === null || miniCart === void 0 ? void 0 : miniCart.totalQty) || 0, 10);

  // App context is the single source of truth. Keeping a second hydrated
  // quantity here would mask every later add-to-cart update.
  var totalQty = Number.isNaN(contextQty) ? 0 : contextQty;

  // Safely check store status
  var isStoreClosed = (setting === null || setting === void 0 ? void 0 : setting.storeStatus) === '0';

  // Recover the cart when a mobile browser restores this page from bfcache.
  // This also retains recovery from localStorage when the session cart is lost.
  useEffect(() => {
    var cancelled = false;
    var syncCartQuantity = async () => {
      try {
        var _data$data;
        var storedCartId = localStorage.getItem('cartId') || (miniCart === null || miniCart === void 0 ? void 0 : miniCart.uuid);
        if (!storedCartId) return;
        var response = await fetch('/api/graphql', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: "query CartQuantity($cartId: String) {\n              cart(id: $cartId) { totalQty }\n            }",
            variables: {
              cartId: storedCartId
            }
          })
        });
        var data = await response.json();
        var qty = Number(data === null || data === void 0 || (_data$data = data.data) === null || _data$data === void 0 || (_data$data = _data$data.cart) === null || _data$data === void 0 ? void 0 : _data$data.totalQty);
        if (cancelled || !Number.isFinite(qty) || qty < 0) return;
        setData(currentData => {
          var currentCart = (currentData === null || currentData === void 0 ? void 0 : currentData.cart) || {};
          if (Number(currentCart.totalQty) === qty) return currentData;
          return _objectSpread(_objectSpread({}, currentData || {}), {}, {
            cart: _objectSpread(_objectSpread({}, currentCart), {}, {
              uuid: currentCart.uuid || storedCartId,
              totalQty: qty
            })
          });
        });
      } catch (_) {
        // Storage or network unavailable: retain the current context count.
      }
    };
    var handlePageShow = event => {
      if (event.persisted) {
        syncCartQuantity();
      }
    };
    if (contextQty <= 0) {
      syncCartQuantity();
    }
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      cancelled = true;
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [contextQty, miniCart === null || miniCart === void 0 ? void 0 : miniCart.uuid, setData]);

  // Trigger shake animation when quantity increases
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (totalQty > prevQtyRef.current) {
      controls.start({
        x: [0, -10, 10, -10, 10, 0],
        rotate: [0, -5, 5, -5, 5, 0],
        transition: {
          duration: 0.5
        }
      });
    }
    prevQtyRef.current = totalQty;
  }, [totalQty, controls]);
  return /*#__PURE__*/React.createElement("div", {
    className: "floating-cart-container relative z-[40] ".concat(isStoreClosed ? 'hidden' : '')
  }, /*#__PURE__*/React.createElement("a", {
    className: "relative flex flex-col items-center gap-1 z-[40]",
    href: cartUrl || '/cart'
  }, /*#__PURE__*/React.createElement(motion.div, {
    className: "relative",
    animate: controls
  }, totalQty > 0 ? /*#__PURE__*/React.createElement("div", {
    className: "w-[5rem] h-[5rem]  bg-primary rounded-full flex md:hidden items-center justify-center"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "34",
    height: "40",
    viewBox: "0 0 34 40",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M28.0117 15.1609L26.0054 15.102L24.0304 38.3274C24.703 38.3471 25.2275 37.77 25.2954 37.2186L28.0117 15.1609Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M28.0117 15.1609L25.2954 37.2186C25.2275 37.77 24.703 38.3471 24.0304 38.3274L26.0054 15.102L28.0117 15.1609Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.08202 15.1609L9.08832 15.102L11.0617 38.262C10.3891 38.2817 9.86462 37.7063 9.79674 37.1564L7.08202 15.1609Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.08202 15.1609L9.79674 37.1564C9.86462 37.7063 10.3891 38.2817 11.0617 38.262L9.08832 15.102L7.08202 15.1609Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20.6322 38.4748L21.7504 15.0629L24.0506 15.0659L21.5648 38.476L20.6322 38.4748Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M21.5648 38.476L20.6322 38.4748L21.7504 15.0629L24.0506 15.0659L21.5648 38.476Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14.4614 38.3982L13.3434 15.0629L11.0432 15.0659L13.5289 38.3994L14.4614 38.3982Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.5289 38.3994L14.4614 38.3982L13.3434 15.0629L11.0432 15.0659L13.5289 38.3994Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M18.153 38.2898L19.0579 14.9404L16.4018 14.9356L17.1814 38.2034L18.153 38.2898Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16.4018 14.9356L17.1814 38.2034L18.153 38.2898L19.0579 14.9404L16.4018 14.9356Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M31.8145 13.2051C31.8145 12.7337 31.4323 12.3516 30.9609 12.3516H3.53125C3.05987 12.3516 2.67773 12.7337 2.67773 13.2051C2.67781 13.6764 3.05991 14.0586 3.53125 14.0586H30.9609C31.4323 14.0586 31.8144 13.6764 31.8145 13.2051ZM7.43457 35.6592C7.56825 36.5744 8.35343 37.2527 9.27832 37.2529H25.2129C26.138 37.2529 26.9229 36.5745 27.0566 35.6592L30.0146 15.4141H4.47754L7.43457 35.6592ZM33.1699 13.2051C33.1699 14.2781 32.4041 15.1714 31.3896 15.3711L28.3984 35.8555C28.1673 37.4364 26.8107 38.6084 25.2129 38.6084H9.27832C7.68077 38.6082 6.32485 37.4362 6.09375 35.8555L3.10156 15.3711C2.08745 15.1711 1.32233 14.2778 1.32227 13.2051C1.32227 11.9852 2.31141 10.9961 3.53125 10.9961H30.9609C32.1808 10.9961 33.1699 11.9853 33.1699 13.2051Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.72473 2.8261C7.50876 2.46939 8.47397 2.70645 8.96301 3.4345L9.05481 3.58684L9.06164 3.59954C9.40674 4.2758 9.24368 5.05227 8.74133 5.55266L8.98449 6.0175C10.494 6.41725 11.8725 7.47102 12.8185 8.49309C13.0877 8.78003 13.4436 9.18511 13.757 9.58098C14.0452 9.94515 14.3785 10.3999 14.5197 10.7529L14.962 11.8583H5.02063L4.98254 11.0917C4.94744 10.3861 5.17419 9.47399 5.45422 8.71087C5.59931 8.31555 5.77045 7.92858 5.95324 7.59172C6.02609 7.45751 6.10645 7.32369 6.19153 7.19524L6.00598 6.84075C5.2878 6.93374 4.54194 6.58873 4.19055 5.9218L4.18762 5.91594C3.72871 5.02661 4.15418 4.01198 5.00207 3.61809L5.00793 3.61516C5.31955 3.47346 5.65987 3.42625 5.98742 3.46575C6.16191 3.19517 6.41295 2.97017 6.71887 2.82805L6.72473 2.8261ZM8.34289 3.96575C8.10665 3.52249 7.53583 3.34245 7.05871 3.5595C6.59138 3.7766 6.39007 4.31232 6.6261 4.75579C6.38993 4.31247 5.81905 4.13258 5.34192 4.34954C4.87466 4.5666 4.67252 5.09779 4.90344 5.54583C5.13464 5.98464 5.71538 6.17883 6.18274 5.96184C6.26141 5.92952 6.33075 5.87911 6.38977 5.83293L7.14758 7.28313C6.51788 7.89289 5.73323 9.96599 5.78723 11.0517H13.7706C13.6343 10.711 13.1232 10.0586 12.6613 9.52434L12.2316 9.04485C11.2624 7.99629 9.86666 6.98047 8.44153 6.71965L7.66907 5.24114C7.74769 5.22728 7.8268 5.20872 7.90051 5.1718C8.37742 4.95454 8.56914 4.4091 8.34289 3.96575Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M17.8833 0C18.9551 0.000251733 19.8244 0.86953 19.8276 1.95215L19.8198 2.14746C19.7467 2.94642 19.1901 3.63459 18.4233 3.8584L18.4272 4.57227C19.367 5.10972 20.1528 6.0545 20.7144 7.07617C21.385 8.29641 21.8156 9.76563 21.7925 11.125L21.7788 11.918H13.9311L13.7485 11.3652C13.5061 10.6348 13.1755 9.92246 13.0288 9.61914C12.9326 9.42017 12.8623 9.17141 12.8784 8.89258L12.9116 8.55566C13.0196 7.76253 13.3392 6.92506 13.7466 6.22168C14.0945 5.62106 14.5693 5.01103 15.1323 4.62207L15.1284 3.87695C14.3623 3.66319 13.7977 2.97824 13.7173 2.18066L13.7075 1.98438C13.7025 0.919348 14.5411 0.0205292 15.6245 0.0166016H15.6274C15.6287 0.0165988 15.63 0.0156252 15.6313 0.015625C16.0528 0.0134577 16.4401 0.150187 16.7573 0.378906C17.0705 0.145803 17.4549 0.00370149 17.8755 0H17.8833ZM15.6313 0.822266C15.0115 0.822425 14.5081 1.34064 14.5132 1.98438C14.5132 2.61778 15.0272 3.14684 15.6421 3.1416C15.7435 3.14157 15.8399 3.1257 15.9311 3.09961L15.9419 5.09375C14.877 5.51138 13.7675 7.47897 13.6831 8.93848C13.6765 9.05267 13.7056 9.16557 13.7554 9.26855C13.8696 9.50483 14.0931 9.98511 14.3071 10.5391L14.5132 11.1113H20.9858C21.0265 8.72428 19.458 5.78482 17.6235 5.07812L17.6138 3.08887C17.7051 3.11499 17.7964 3.13085 17.8979 3.13086C18.523 3.12562 19.0261 2.59141 19.021 1.95801C19.0208 1.31443 18.508 0.806893 17.8833 0.806641C17.2736 0.811875 16.7702 1.32473 16.77 1.95801C16.7597 1.32473 16.2461 0.817032 15.6313 0.822266Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M24.5977 3.66332C25.1439 2.83769 26.2205 2.58302 27.0688 3.02528L27.2359 3.12402L27.2368 3.12503C27.5755 3.34921 27.8217 3.66531 27.9626 4.01996C28.3441 4.0062 28.733 4.105 29.0741 4.32937C29.9537 4.90656 30.2074 6.08544 29.6259 6.97285L29.6249 6.9738C29.1519 7.69273 28.2587 7.99188 27.4624 7.74701L27.1034 8.29033C27.5888 9.22759 27.6952 10.3878 27.5476 11.4973L27.4511 12.2184L21.1774 12.0325L21.1348 11.2956C21.1302 11.217 21.1382 11.1436 21.1384 11.1413C21.1406 11.1185 21.144 11.0922 21.1463 11.0722C21.1514 11.0277 21.1577 10.9759 21.1638 10.9115C21.1758 10.7835 21.1883 10.6053 21.1871 10.3866C21.1848 9.94806 21.1287 9.34542 20.9109 8.6618L20.9064 8.64897C20.8909 8.59741 20.8709 8.54083 20.8407 8.45846L20.6157 7.84411L21.1721 7.49715C22.1463 6.89009 23.2827 6.48791 24.3601 6.5384L24.7427 5.95768C24.2046 5.32606 24.1241 4.38374 24.5966 3.66524L24.5977 3.66332ZM26.7919 3.79769C26.2907 3.46789 25.6054 3.60187 25.2703 4.10824C24.9406 4.60963 25.0804 5.3059 25.5817 5.63571C25.6626 5.68961 25.7541 5.72836 25.8409 5.75672L24.7596 7.39729C23.8055 7.19887 22.6499 7.52652 21.5983 8.18195C21.6269 8.26021 21.6558 8.33876 21.6793 8.41687C22.1732 9.96709 21.9351 11.1557 21.9393 11.2481L26.7477 11.3905C26.907 10.1925 26.7241 9.04651 26.1483 8.27571L27.2132 6.66005C27.2731 6.72892 27.3387 6.79291 27.4197 6.84692C27.8991 7.16098 28.5366 7.04899 28.886 6.62071L28.9512 6.5308C29.2864 6.01925 29.1415 5.33832 28.6301 5.00304C28.1288 4.67341 27.4484 4.80811 27.1132 5.31959C27.4484 4.80804 27.2982 4.13281 26.7919 3.79769Z",
    fill: "white"
  }))) : /*#__PURE__*/React.createElement("div", {
    className: "w-[5rem] h-[5rem] bg-primary rounded-full md:hidden flex  items-center justify-center"
  }, /*#__PURE__*/React.createElement("div", {
    className: "mb-2"
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "34",
    height: "40",
    viewBox: "0 0 34 40",
    fill: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M28.0116 15.161L26.0053 15.1021L24.0303 38.3274C24.7029 38.3471 25.2274 37.7701 25.2953 37.2187L28.0116 15.161Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M28.0116 15.161L25.2953 37.2187C25.2274 37.7701 24.7029 38.3471 24.0303 38.3274L26.0053 15.1021L28.0116 15.161Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.08203 15.161L9.08833 15.1021L11.0617 38.2621C10.3891 38.2818 9.86463 37.7063 9.79675 37.1564L7.08203 15.161Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.08203 15.161L9.79675 37.1564C9.86463 37.7063 10.3891 38.2818 11.0617 38.2621L9.08833 15.1021L7.08203 15.161Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20.6323 38.4749L21.7505 15.063L24.0507 15.066L21.5649 38.4761L20.6323 38.4749Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M21.5649 38.4761L20.6323 38.4749L21.7505 15.063L24.0507 15.066L21.5649 38.4761Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14.4612 38.3983L13.3432 15.063L11.043 15.066L13.5287 38.3995L14.4612 38.3983Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.5287 38.3995L14.4612 38.3983L13.3432 15.063L11.043 15.066L13.5287 38.3995Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M18.1531 38.2897L19.058 14.9403L16.4019 14.9355L17.1815 38.2033L18.1531 38.2897Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16.4019 14.9355L17.1815 38.2033L18.1531 38.2897L19.058 14.9403L16.4019 14.9355Z",
    fill: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M31.8145 13.2051C31.8145 12.7337 31.4323 12.3516 30.9609 12.3516H3.53125C3.05987 12.3516 2.67773 12.7337 2.67773 13.2051C2.67781 13.6764 3.05991 14.0586 3.53125 14.0586H30.9609C31.4323 14.0586 31.8144 13.6764 31.8145 13.2051ZM7.43457 35.6592C7.56825 36.5744 8.35343 37.2527 9.27832 37.2529H25.2129C26.138 37.2529 26.9229 36.5745 27.0566 35.6592L30.0146 15.4141H4.47754L7.43457 35.6592ZM33.1699 13.2051C33.1699 14.2781 32.4041 15.1714 31.3896 15.3711L28.3984 35.8555C28.1673 37.4364 26.8107 38.6084 25.2129 38.6084H9.27832C7.68077 38.6082 6.32485 37.4362 6.09375 35.8555L3.10156 15.3711C2.08745 15.1711 1.32233 14.2778 1.32227 13.2051C1.32227 11.9852 2.31141 10.9961 3.53125 10.9961H30.9609C32.1808 10.9961 33.1699 11.9853 33.1699 13.2051Z",
    fill: "white"
  })))), /*#__PURE__*/React.createElement("span", {
    className: "absolute !font-rubik md:hidden bottom-1 -right-1 bg-[#060708] text-[#F9F9F9] text-[1rem] rounded-full flex items-center justify-center min-w-[1.8rem] h-[1.8rem]  font-bold z-10"
  }, totalQty))));
}
FloatingCart.propTypes = {
  cartUrl: PropTypes.string,
  cart: PropTypes.shape({
    totalQty: PropTypes.number
  })
};
FloatingCart.defaultProps = {
  cartUrl: '/cart',
  cart: null
};
;
export var query = "\n  query Query {\n    cartUrl: url(routeId: \"cart\"),\n    cart(id: getContextValue(\"cartId\", null)) {\n      totalQty\n    },\n    setting{\n      storeStatus\n    }\n  }\n";